import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Card, CardBody, Col, Row } from "reactstrap";

import { DefaultButton, H3, P, SecondaryButton } from "../../AbstractElements";
import DateRangeDisplay from "../../CommonElements/DateRangeDisplay";
import Divider from "../../CommonElements/Divider";
import FixedFooter from "../../CommonElements/FixedFooter";
import StarLikertScale from "../../CommonElements/StarLikertScale";
import StatusDisplay from "../../CommonElements/StatusDisplay";
import CourseContentTable from "../../container/FeedbackCourseContent";
import { useAppDispatch, useAppSelector } from "../../ReduxToolkit/Hooks";
import {
  getTrainingImpression,
  setTrainingImpressionToInitial,
  updateTrainingImpression,
} from "../../ReduxToolkit/Reducers/CourseSlice";
import {
  Frequency,
  Instructor,
  LearningOutcomes,
  Location,
  Participant,
  PleaseRateYourLevelAgreement,
  Room,
  SaveAsDraft,
  Submit,
} from "../../utils/Constant";
import {
  formatString,
  trainingImpressionFrequencyMap,
} from "../../utils/helper/helper";
import useIsMobile from "../../utils/helper/responsive";

interface QuestionData {
  id: number;
  category: string;
  learning_outcome_id: number;
  question_text: string;
  answer_type: string;
  response?: string | number | null;
}

const TrainingImpressions = () => {
  const isMobile = useIsMobile();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const trainingFeedbackId = queryParams.get("training_feedback_id");
  const { trainingImpression } = useAppSelector(state => state.course);
  const [questionsData, setQuestionsData] = useState<QuestionData[]>([]);

  useEffect(() => {
    dispatch(getTrainingImpression({ trainingFeedbackId }));
  }, [dispatch]);

  useEffect(() => {
    if (trainingImpression?.training_feedback_response?.length > 0) {
      const convertedQuestions: QuestionData[] =
        trainingImpression.training_feedback_response.map(item => ({
          id: item.training_feedback_response_id,
          learning_outcome_id: item.learning_outcome_id,
          category: LearningOutcomes,
          question_text: item.learning_outcome,
          answer_type: "star",
          response: item.rating,
        }));
      setQuestionsData(convertedQuestions);
    }
  }, [trainingImpression]);

  const handleFinish = async type => {
    if (type === "submitted") {
      const payload = {
        status: "submitted",
        training_feedback_id: parseInt(trainingFeedbackId || "0", 10),
        training_feedback_response: questionsData.map(item => ({
          training_feedback_response_id: item.id,
          learning_outcome_id: item.learning_outcome_id,
          rating: item.response
            ? parseInt(item.response?.toString() || "0", 10)
            : null,
        })),
      };

      dispatch(updateTrainingImpression({ payload })).then(() => {
        dispatch(setTrainingImpressionToInitial());
        navigate(`${process.env.PUBLIC_URL}/action-menu`);
      });
    } else if (type === "draft") {
      const payload = {
        status: "draft",
        training_feedback_id: parseInt(trainingFeedbackId || "0", 10),
        training_feedback_response: questionsData.map(item => ({
          training_feedback_response_id: item.id,
          learning_outcome_id: item.learning_outcome_id,
          rating: item.response
            ? parseInt(item.response?.toString() || "0", 10)
            : null,
        })),
      };
      dispatch(updateTrainingImpression({ payload })).then(() => {
        dispatch(setTrainingImpressionToInitial());
        navigate(`${process.env.PUBLIC_URL}/action-menu`);
      });
    }
  };

  const handleRating = (id: number, rate: number) => {
    setQuestionsData(prevData =>
      prevData.map(question =>
        question.id === id
          ? { ...question, response: rate.toString() }
          : question
      )
    );
  };

  const handleInputChange = (id: number, value: string) => {
    setQuestionsData(prevData =>
      prevData.map(question =>
        question.id === id ? { ...question, response: value } : question
      )
    );
  };

  const groupQuestionsByCategory = () => {
    return (
      questionsData?.reduce(
        (grouped, question) => {
          if (!grouped[question.category]) {
            grouped[question.category] = [];
          }
          grouped[question.category].push(question);
          return grouped;
        },
        {} as Record<string, QuestionData[]>
      ) || {}
    );
  };

  return (
    <div className="page-body">
      <div className="container-fluid">
        <Card>
          <CardBody className="m-2">
            <H3 className="fw-bold">{trainingImpression?.course_name}</H3>
            <Row className="mt-3">
              <Col lg="4" className="mt-1">
                <DateRangeDisplay
                  startDate={trainingImpression?.start_date}
                  endDate={trainingImpression?.end_date}
                />
              </Col>
              <Col className="d-flex justify-content-between mt-3"></Col>
            </Row>
            <div className={`${!isMobile ? "d-flex gap-5 mt-4" : "gap-3"}`}>
              <div className={isMobile && "mt-2"}>
                <StatusDisplay
                  label={Location}
                  number={trainingImpression?.location_name}
                />
              </div>
              <div className={isMobile && "mt-2"}>
                <StatusDisplay
                  label={Room}
                  number={trainingImpression?.room_name}
                />
              </div>
              <div className={isMobile && "mt-2"}>
                <StatusDisplay
                  label={Instructor}
                  number={trainingImpression?.primary_trainer_name}
                />
              </div>
              <div className={isMobile && "mt-2"}>
                <StatusDisplay
                  label={Participant}
                  number={trainingImpression?.user_name}
                />
              </div>
              <div className={isMobile && "mt-2"}>
                <StatusDisplay
                  label={Frequency}
                  number={
                    trainingImpressionFrequencyMap[
                      trainingImpression?.feedback_phase
                    ]
                  }
                />
              </div>
            </div>

            <div className="mb-2">
              <Divider />
            </div>
            <P className="mt-1 mb-1">{PleaseRateYourLevelAgreement}</P>
            <div className="mt-2">
              <StarLikertScale />
            </div>
            <div className="mt-3">
              {Object.entries(groupQuestionsByCategory())?.map(
                ([category, questionsList]) => (
                  <div className="mb-4" key={category}>
                    <CourseContentTable
                      key={category}
                      questions={questionsList}
                      handleRating={handleRating}
                      handleInputChange={handleInputChange}
                      category={formatString(category)}
                      review={false}
                    />
                  </div>
                )
              )}
            </div>
          </CardBody>
        </Card>
        <FixedFooter>
          <SecondaryButton onClick={() => handleFinish("draft")}>
            {SaveAsDraft}
          </SecondaryButton>
          <DefaultButton
            onClick={() => handleFinish("submitted")}
            color="primary"
          >
            {Submit}
          </DefaultButton>
        </FixedFooter>
      </div>
    </div>
  );
};

export default TrainingImpressions;
